import React, { useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useDrop } from 'react-dnd';
import PropTypes from 'prop-types';

import RemoteUserOverlay from './RemoteUserOverlay';
import ColumnItem from './ColumnItem/ColumnItem';
import * as selectedElementsActions from '../../../../../actions/selectedElements';
import * as selectedBlocksActions from '../../../../../actions/selectedBlocks';
import * as columnItemActions from '../../../../../actions/columnItem';
import * as itemTypes from '../../../../../constants/itemTypes/itemTypes';
import { elementContentType, remoteUserType } from '../../../../../types';
import * as Styled from './styles';
import businessRules from '../../../../../constants/businessRules/businessRules';
import handleKeyPress from '../../../../../common/handleKeyPress';
import eventService from '../../../../../common/eventService';
import { ERR_COLUMN_ITEM_MAX_ELEMENTS } from '../../../../../constants/Errors';
import { getMousePosition } from '../../../../../common/getMousePosition';

const ColumnItemContainer = (props) => {
    const {
        blockIndex,
        columnIdx,
        structureLocked,
        elmIdx,
        numElements,
        content,
        type,
        width,
        isRemoteSelected,
        selectedElements,
        settings,
        browser,
        user,
        dispatch,
    } = props;
    const ref = useRef(null);
    const [hoverPosition, setHoverPosition] = useState(null);
    const multipleElementsSelected = Object.keys(selectedElements.local).length > 1;
    const elementIsSelected = !!selectedElements.local[elmIdx];
    const { canSelectMultipleElements } = settings;
    const { modifierKeyPressed } = browser;

    const [{ isOverCurrent }, drop] = useDrop(
        {
            accept: [itemTypes.STATIC_BLOCK_COLUMN_ITEM, itemTypes.CUSTOMIZED_BLOCK_COLUMN_ITEM],
            hover: (item, monitor) => {
                if (!ref.current) {
                    return;
                }
                if (item.draggedColumnItemIdx === elmIdx) {
                    setHoverPosition(null);
                    return;
                }
                const { isInTopHalf } = getMousePosition(ref, monitor);
                setHoverPosition(isInTopHalf ? 'before' : 'after');
            },
            drop: (item, monitor) => {
                if (!ref.current) {
                    return;
                }
                const showMaxColumnItemError =
                    numElements >= businessRules.MAXIMUM_ALLOWABLE_BLOCK_COLUMN_ITEMS &&
                    item.draggedColumnIdx !== columnIdx;
                if (showMaxColumnItemError) {
                    eventService.notify(ERR_COLUMN_ITEM_MAX_ELEMENTS, true);
                    return;
                }
                const dragType = monitor.getItemType();
                if (dragType === itemTypes.STATIC_BLOCK_COLUMN_ITEM) {
                    const { itemType } = item;
                    dispatch(
                        columnItemActions.addNewColumnItem({
                            blockIndex,
                            columnIdx,
                            elmIdx,
                            itemType,
                            position: hoverPosition,
                        })
                    );
                } else if (dragType === itemTypes.CUSTOMIZED_BLOCK_COLUMN_ITEM) {
                    const {
                        draggedBlockIndex,
                        draggedColumnIdx,
                        draggedColumnItemIdx,
                        content: newContent,
                        itemType,
                    } = item;
                    dispatch(
                        columnItemActions.moveColumnItemContentToAnotherColumn({
                            draggedBlockIndex,
                            draggedColumnIdx,
                            draggedColumnItemIdx,
                            blockIndex,
                            columnIdx,
                            elmIdx,
                            content: newContent,
                            type: itemType,
                            position: hoverPosition,
                        })
                    );
                }
            },
            collect: (monitor) => ({
                isOver: monitor.isOver(),
                isOverCurrent: monitor.isOver({ shallow: true }),
            }),
        },
        [blockIndex, columnIdx, elmIdx, hoverPosition]
    );

    drop(ref);

    const selectElement = () => {
        if (isRemoteSelected) {
            return;
        }
        const selectedElementData = {
            elmIdx,
            data: {
                blockIndex,
                columnIdx,
                type,
                width,
            },
        };
        dispatch(selectedBlocksActions.removeAllSelectedBlocks());
        dispatch(selectedBlocksActions.addSelectedBlock({ index: blockIndex }));
        if (modifierKeyPressed) {
            if (!elementIsSelected) {
                dispatch(selectedElementsActions.addSelectedElement(selectedElementData));
                return;
            }
        }
        if (elementIsSelected) {
            // empty if block?
        } else if (canSelectMultipleElements) {
            dispatch(selectedElementsActions.addSelectedElement(selectedElementData));
        } else {
            dispatch(selectedElementsActions.removeAllSelectedElements());
            dispatch(selectedElementsActions.addSelectedElement(selectedElementData));
        }
    };

    return (
        <div
            ref={ref}
            key={elmIdx}
            style={{
                position: 'relative',
                display: 'block',
            }}
            onMouseDown={(event) => {
                event.stopPropagation(); // prevent CanvasBlockContent onMouseDown from firing when clicking an element
            }}
            onClick={selectElement}
            onKeyDown={(e) => handleKeyPress(e, selectElement)}
            onFocus={selectElement}
            role="button"
            tabIndex={0}
        >
            <Styled.DropZoneBar isVisible={isOverCurrent && hoverPosition === 'before'} />
            <div style={{ position: 'relative' }}>
                <RemoteUserOverlay content={content} isRemoteSelected={isRemoteSelected} user={user} elmIdx={elmIdx}>
                    <ColumnItem
                        blockIndex={blockIndex}
                        columnIdx={columnIdx}
                        structureLocked={structureLocked}
                        elmIdx={elmIdx}
                        numElements={numElements}
                        content={content}
                        type={type}
                        width={width}
                        elementIsSelected={elementIsSelected}
                        multipleElementsSelected={multipleElementsSelected}
                    />
                </RemoteUserOverlay>
            </div>
            <Styled.DropZoneBar isVisible={isOverCurrent && hoverPosition === 'after'} />
        </div>
    );
};

const mapStateToProps = (state) => ({
    selectedElements: state.selectedElements,
    selectedBlocks: state.selectedBlocks,
    settings: state.settings,
    browser: state.browser,
});

ColumnItemContainer.propTypes = {
    blockIndex: PropTypes.string.isRequired,
    columnIdx: PropTypes.string.isRequired,
    structureLocked: PropTypes.bool.isRequired,
    elmIdx: PropTypes.string.isRequired,
    numElements: PropTypes.number.isRequired,
    content: elementContentType.isRequired,
    type: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
    selectedElements: PropTypes.object.isRequired,
    settings: PropTypes.object.isRequired,
    browser: PropTypes.shape({
        canSelectMultipleElements: PropTypes.bool.isRequired,
        browserName: PropTypes.string.isRequired,
        windowInnerWidth: PropTypes.number,
        modifierKeyPressed: PropTypes.bool.isRequired,
        shiftKeyPressed: PropTypes.bool.isRequired,
        leftPanelCollapsed: PropTypes.bool.isRequired,
    }).isRequired,
    dispatch: PropTypes.func.isRequired,
    isRemoteSelected: PropTypes.bool.isRequired,
    user: remoteUserType,
};

ColumnItemContainer.defaultProps = {
    user: null,
};

export default connect(mapStateToProps)(ColumnItemContainer);
